/**
 * C.5 — Resizable three-pane layout hook.
 *
 * Owns the column widths for Tree | Editor A [| Editor B] | Graph and
 * exposes mousedown handlers for the inter-column grippers. Pane widths
 * are persisted in localStorage so layout survives reload.
 *
 * Q2 contract: grippers must clamp to minimum sizes so panes can never
 * collapse fully. Editor B is only present when `splitOpen` is true; when
 * it's closed, its width budget is folded back into editor A.
 *
 * Math:
 *   total = container width
 *   tree           = clamp(treeWidth, treeMin, total - everythingElseMin)
 *   editorA + B    = total - tree - graph (- splitter widths)
 *   graph          = clamp(graphWidth, graphMin, total - everythingElseMin)
 *
 * All widths are stored as fractions of the visible container width so
 * the layout adapts when the window itself is resized.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'repancy.obsidian-vault.layout.v1';

export interface ResizablePanesConfig {
  /** Default fractions when nothing is persisted yet. */
  defaultTreeFraction: number;
  defaultGraphFraction: number;
  /** Inside the editor column, A's share of (A+B) when split is open. */
  defaultEditorSplitFraction: number;
  /** Minimum pixel widths so panes never collapse to nothing. */
  treeMinPx: number;
  editorMinPx: number;
  graphMinPx: number;
  /** Gripper thickness in pixels (must match CSS). */
  gripperPx: number;
}

export const DEFAULT_PANES_CONFIG: ResizablePanesConfig = {
  defaultTreeFraction: 0.22,
  defaultGraphFraction: 0.28,
  defaultEditorSplitFraction: 0.5,
  treeMinPx: 140,
  editorMinPx: 240,
  graphMinPx: 160,
  gripperPx: 6,
};

interface StoredLayout {
  treeFraction: number;
  graphFraction: number;
  editorSplitFraction: number;
}

function readStoredLayout(): Partial<StoredLayout> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<StoredLayout>;
    return parsed;
  } catch {
    return {};
  }
}

function writeStoredLayout(layout: StoredLayout): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* localStorage may be unavailable (private mode); silently ignore. */
  }
}

export interface PaneWidths {
  /** Pixel widths in order: tree, editor A, editor B (0 if !splitOpen), graph. */
  tree: number;
  editorA: number;
  editorB: number;
  graph: number;
  /** Total container width as measured by ResizeObserver. */
  total: number;
}

export interface UseResizablePanesResult {
  /** Ref to attach to the outer flex container so we can read its width. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Live pixel widths for the four pane segments. */
  widths: PaneWidths;
  /** Whether the editor column is currently split into A and B. */
  splitOpen: boolean;
  /** Mousedown handler for the tree↔editor gripper. */
  onTreeGripperDown: (e: React.MouseEvent) => void;
  /** Mousedown handler for the editor A↔B gripper (only meaningful when splitOpen). */
  onEditorSplitGripperDown: (e: React.MouseEvent) => void;
  /** Mousedown handler for the editor↔graph gripper. */
  onGraphGripperDown: (e: React.MouseEvent) => void;
  /** True while the user is actively dragging a gripper (for cursor styling). */
  activeGripper: 'tree' | 'editorSplit' | 'graph' | null;
  /** Imperative API: open the editor split (when a wikilink is clicked). */
  openSplit: () => void;
  /** Imperative API: close the editor split (return to single editor pane). */
  closeSplit: () => void;
}

export function useResizablePanes(
  config: ResizablePanesConfig = DEFAULT_PANES_CONFIG,
): UseResizablePanesResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [total, setTotal] = useState(0);
  const stored = useRef(readStoredLayout()).current;

  const [treeFraction, setTreeFraction] = useState(
    stored.treeFraction ?? config.defaultTreeFraction,
  );
  const [graphFraction, setGraphFraction] = useState(
    stored.graphFraction ?? config.defaultGraphFraction,
  );
  const [editorSplitFraction, setEditorSplitFraction] = useState(
    stored.editorSplitFraction ?? config.defaultEditorSplitFraction,
  );
  const [splitOpen, setSplitOpen] = useState(false);
  const [activeGripper, setActiveGripper] = useState<
    'tree' | 'editorSplit' | 'graph' | null
  >(null);

  // Persist on change (cheap; localStorage write is sync but tiny).
  useEffect(() => {
    writeStoredLayout({ treeFraction, graphFraction, editorSplitFraction });
  }, [treeFraction, graphFraction, editorSplitFraction]);

  // Observe container size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setTotal(Math.round(w));
    });
    ro.observe(el);
    setTotal(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // Compute clamped widths.
  const gripperCount = splitOpen ? 3 : 2;
  const grippers = gripperCount * config.gripperPx;
  const available = Math.max(0, total - grippers);

  const minEditorTotal = splitOpen ? config.editorMinPx * 2 : config.editorMinPx;
  const requestedTree = Math.round(treeFraction * total);
  const requestedGraph = Math.round(graphFraction * total);

  const maxTree = Math.max(
    config.treeMinPx,
    available - config.graphMinPx - minEditorTotal,
  );
  const tree = Math.min(Math.max(requestedTree, config.treeMinPx), maxTree);

  const maxGraph = Math.max(
    config.graphMinPx,
    available - tree - minEditorTotal,
  );
  const graph = Math.min(Math.max(requestedGraph, config.graphMinPx), maxGraph);

  const editorTotal = Math.max(minEditorTotal, available - tree - graph);
  let editorA = editorTotal;
  let editorB = 0;
  if (splitOpen) {
    const requestedA = Math.round(editorSplitFraction * editorTotal);
    editorA = Math.min(
      Math.max(requestedA, config.editorMinPx),
      editorTotal - config.editorMinPx,
    );
    editorB = editorTotal - editorA;
  }

  const widths: PaneWidths = {
    tree,
    editorA,
    editorB,
    graph,
    total,
  };

  // Drag handlers — shared body listener wired in for each gripper kind.
  const startDrag = useCallback(
    (kind: 'tree' | 'editorSplit' | 'graph') =>
      (e: React.MouseEvent) => {
        e.preventDefault();
        setActiveGripper(kind);
        const containerEl = containerRef.current;
        if (!containerEl) return;
        const rect = containerEl.getBoundingClientRect();

        const onMove = (ev: MouseEvent) => {
          const xWithin = ev.clientX - rect.left;
          if (rect.width <= 0) return;
          if (kind === 'tree') {
            const f = xWithin / rect.width;
            setTreeFraction(Math.max(0.05, Math.min(0.6, f)));
          } else if (kind === 'graph') {
            const f = (rect.width - xWithin) / rect.width;
            setGraphFraction(Math.max(0.08, Math.min(0.6, f)));
          } else {
            // editorSplit: x is relative to the start of editor A, which
            // begins at `tree + 1 gripper` and ends at `tree + 1 + editorTotal`.
            const editorStart =
              tree + config.gripperPx; /* tree gripper */
            const editorWidth = editorTotal;
            if (editorWidth <= 0) return;
            const local = (xWithin - editorStart) / editorWidth;
            setEditorSplitFraction(Math.max(0.2, Math.min(0.8, local)));
          }
        };
        const onUp = () => {
          setActiveGripper(null);
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      },
    [config.gripperPx, editorTotal, tree],
  );

  const openSplit = useCallback(() => setSplitOpen(true), []);
  const closeSplit = useCallback(() => setSplitOpen(false), []);

  return {
    containerRef,
    widths,
    splitOpen,
    onTreeGripperDown: startDrag('tree'),
    onEditorSplitGripperDown: startDrag('editorSplit'),
    onGraphGripperDown: startDrag('graph'),
    activeGripper,
    openSplit,
    closeSplit,
  };
}
